import { useCallback, useRef } from 'react';

import { useDisclosure } from '@mantine/hooks';
import {
  DistanceUnitEnum,
  SCORE_TYPES_REQUIRING_DISTANCE_UNIT,
  SCORE_TYPES_REQUIRING_WEIGHT_UNIT,
  ScoreTypeEnum,
  WeightUnitEnum,
} from 'types/enums';
import { v4 as uuidv4 } from 'uuid';

import { BenchmarkDraft } from './ModalSaveAsBenchmark';
import {
  SectionMovement,
  SelectedBenchmark,
  WorkoutSectionData,
  WorkoutSectionFormHandle,
} from '../../../../../forms/workoutBuilder/types';

interface UseWorkoutSectionResult {
  getSectionTitle: () => string;
  handleAddMovement: () => void;
  handleMovementChange: (
    movementIndex: number,
    movementId: string | null,
    movementName: string | null,
    defaultScoreType: ScoreTypeEnum | null,
  ) => void;
  handleMovementScoringChange: (movementIndex: number, updates: Partial<SectionMovement>) => void;
  handleDeleteMovement: (movementIndex: number) => void;
  disableScoreEntireSection: () => void;
  enableSectionAsOneOff: () => void;
  enableSectionAsKeptBenchmark: (draft: BenchmarkDraft) => void;
  revertToOneOff: () => void;
  linkBenchmark: (benchmark: SelectedBenchmark) => void;
  unlinkBenchmark: () => void;
  handleToggleLevel: (level: 'levelScaled' | 'levelRx' | 'levelRxPlus', enabled: boolean) => void;
  sectionOpened: boolean;
  toggleSection: () => void;
}

const buildEmptyMovement = (): SectionMovement => ({
  id: uuidv4(),
  movementId: '',
  scoreType: null,
  distanceUnit: null,
  weightUnit: null,
  scoreSets: [],
});

const isHtmlEmpty = (html: string): boolean => html.replace(/<[^>]*>/g, '').trim() === '';

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const applyScoreTypeDefaults = (
  current: SectionMovement,
  nextScoreType: ScoreTypeEnum | null,
): Partial<SectionMovement> => {
  const updates: Partial<SectionMovement> = { scoreType: nextScoreType };

  if (nextScoreType && SCORE_TYPES_REQUIRING_DISTANCE_UNIT.has(nextScoreType)) {
    updates.distanceUnit = current.distanceUnit ?? DistanceUnitEnum.MILES;
  } else {
    updates.distanceUnit = null;
  }

  if (nextScoreType && SCORE_TYPES_REQUIRING_WEIGHT_UNIT.has(nextScoreType)) {
    updates.weightUnit = current.weightUnit ?? WeightUnitEnum.LB;
  } else {
    updates.weightUnit = null;
  }

  if (nextScoreType === ScoreTypeEnum.WEIGHT_SETS_REPS) {
    updates.scoreSets = current.scoreSets.length > 0 ? current.scoreSets : [{ setIndex: 0, targetReps: 1 }];
  } else {
    updates.scoreSets = [];
  }

  return updates;
};

export function useWorkoutSection(
  form: WorkoutSectionFormHandle,
  index: number,
  section: WorkoutSectionData,
): UseWorkoutSectionResult {
  const [sectionOpened, { toggle: toggleSection }] = useDisclosure(true);
  const lastAutofilledDescription = useRef<string | null>(null);

  const getSectionTitle = useCallback((): string => {
    if (section.name) return `Section ${section.letter} - ${section.name}`;
    return `Section ${section.letter}`;
  }, [section.name, section.letter]);

  const handleAddMovement = useCallback((): void => {
    const currentMovements = form.values.sections[index].movements;
    form.setFieldValue(`sections.${index}.movements`, [...currentMovements, buildEmptyMovement()]);
  }, [form, index]);

  const handleUpdateMovement = useCallback(
    (movementIndex: number, updates: Partial<SectionMovement>): void => {
      const currentMovements = form.values.sections[index].movements;
      const updatedMovements = currentMovements.map((m, i) =>
        i === movementIndex ? { ...m, ...updates } : m,
      );
      form.setFieldValue(`sections.${index}.movements`, updatedMovements);
    },
    [form, index],
  );

  const handleMovementChange = useCallback(
    (
      movementIndex: number,
      movementId: string | null,
      movementName: string | null,
      defaultScoreType: ScoreTypeEnum | null,
    ): void => {
      const current = form.values.sections[index].movements[movementIndex];
      const baseUpdates: Partial<SectionMovement> = {
        movementId: movementId || '',
        movementName: movementName ?? undefined,
      };

      if (!movementId) {
        handleUpdateMovement(movementIndex, {
          ...baseUpdates,
          scoreType: null,
          distanceUnit: null,
          weightUnit: null,
          scoreSets: [],
        });
        return;
      }

      // Pre-fill scoreType from the library default only when the field is still empty,
      // so a coach's prior selection isn't overwritten when they swap movements.
      if (current.scoreType === null && defaultScoreType !== null) {
        handleUpdateMovement(movementIndex, {
          ...baseUpdates,
          ...applyScoreTypeDefaults(current, defaultScoreType),
        });
        return;
      }

      handleUpdateMovement(movementIndex, baseUpdates);
    },
    [form, index, handleUpdateMovement],
  );

  const handleMovementScoringChange = useCallback(
    (movementIndex: number, updates: Partial<SectionMovement>): void => {
      const current = form.values.sections[index].movements[movementIndex];
      // Whenever the score type itself changes, layer in the appropriate unit / score_sets
      // defaults so the form stays consistent with the backend validators.
      if ('scoreType' in updates && updates.scoreType !== current.scoreType) {
        handleUpdateMovement(movementIndex, {
          ...updates,
          ...applyScoreTypeDefaults(current, updates.scoreType ?? null),
        });
        return;
      }
      handleUpdateMovement(movementIndex, updates);
    },
    [form, index, handleUpdateMovement],
  );

  const handleDeleteMovement = useCallback(
    (movementIndex: number): void => {
      const currentMovements = form.values.sections[index].movements;
      const updatedMovements = currentMovements.filter((_, i) => i !== movementIndex);
      form.setFieldValue(`sections.${index}.movements`, updatedMovements);
    },
    [form, index],
  );

  const buildSectionMovement = useCallback(
    (
      benchmarkId: string,
      benchmarkName: string | undefined,
      scoreType: ScoreTypeEnum | null,
      base?: SectionMovement,
    ): SectionMovement => {
      const start = base ?? buildEmptyMovement();
      const typed =
        scoreType && start.scoreType !== scoreType
          ? { ...start, ...applyScoreTypeDefaults(start, scoreType) }
          : start;
      return { ...typed, movementId: benchmarkId, movementName: benchmarkName };
    },
    [],
  );

  const disableScoreEntireSection = useCallback((): void => {
    const basePath = `sections.${index}`;
    form.setFieldValue(`${basePath}.scoreEntireSection`, false);
    form.setFieldValue(`${basePath}.saveAsBenchmark`, true);
    form.setFieldValue(`${basePath}.movements`, []);
    // Levels can only ever be offered on a Score Entire Section config — clear them so a
    // per-movement section never silently carries a combination the backend would reject.
    form.setFieldValue(`${basePath}.levelScaled`, false);
    form.setFieldValue(`${basePath}.levelRx`, false);
    form.setFieldValue(`${basePath}.levelRxPlus`, false);
  }, [form, index]);

  const enableSectionAsOneOff = useCallback((): void => {
    const basePath = `sections.${index}`;
    form.setFieldValue(`${basePath}.scoreEntireSection`, true);
    form.setFieldValue(`${basePath}.saveAsBenchmark`, false);
    form.setFieldValue(`${basePath}.movements`, [buildEmptyMovement()]);
  }, [form, index]);

  const enableSectionAsKeptBenchmark = useCallback(
    (draft: BenchmarkDraft): void => {
      // No id yet — the benchmark is created from this section at workout-save.
      const basePath = `sections.${index}`;
      const base = buildEmptyMovement();
      const movement = draft.scoreType
        ? { ...base, ...applyScoreTypeDefaults(base, draft.scoreType) }
        : base;
      form.setFieldValue(`${basePath}.scoreEntireSection`, true);
      form.setFieldValue(`${basePath}.saveAsBenchmark`, true);
      form.setFieldValue(`${basePath}.movements`, [movement]);
      if (draft.name.trim()) form.setFieldValue(`${basePath}.name`, draft.name.trim());
    },
    [form, index],
  );

  const revertToOneOff = useCallback((): void => {
    // Drop the pending "save as benchmark" but keep the section scored (one-off), score type intact.
    form.setFieldValue(`sections.${index}.saveAsBenchmark`, false);
  }, [form, index]);

  const linkBenchmark = useCallback(
    (benchmark: SelectedBenchmark): void => {
      const basePath = `sections.${index}`;
      const currentSection = form.values.sections[index];
      const current = currentSection.movements[0];
      const scoreType = benchmark.defaultScoreType ?? current?.scoreType ?? null;
      form.setFieldValue(`${basePath}.scoreEntireSection`, true);
      form.setFieldValue(`${basePath}.saveAsBenchmark`, true);
      form.setFieldValue(`${basePath}.movements`, [
        buildSectionMovement(benchmark.id, benchmark.name, scoreType, current),
      ]);
      const overwritable =
        isHtmlEmpty(currentSection.description) ||
        currentSection.description === lastAutofilledDescription.current;
      if (overwritable && benchmark.description) {
        const autofill = `<p><strong>${escapeHtml(benchmark.name)}</strong></p>${benchmark.description}`;
        form.setFieldValue(`${basePath}.description`, autofill);
        lastAutofilledDescription.current = autofill;
      }
    },
    [form, index, buildSectionMovement],
  );

  const unlinkBenchmark = useCallback((): void => {
    const basePath = `sections.${index}`;
    const current = form.values.sections[index].movements[0] ?? buildEmptyMovement();
    form.setFieldValue(`${basePath}.saveAsBenchmark`, false);
    form.setFieldValue(`${basePath}.movements`, [{ ...current, movementId: '', movementName: undefined }]);
  }, [form, index]);

  const handleToggleLevel = useCallback(
    (level: 'levelScaled' | 'levelRx' | 'levelRxPlus', enabled: boolean): void => {
      form.setFieldValue(`sections.${index}.${level}`, enabled);
    },
    [form, index],
  );

  return {
    getSectionTitle,
    handleAddMovement,
    handleMovementChange,
    handleMovementScoringChange,
    handleDeleteMovement,
    disableScoreEntireSection,
    enableSectionAsOneOff,
    enableSectionAsKeptBenchmark,
    revertToOneOff,
    linkBenchmark,
    unlinkBenchmark,
    handleToggleLevel,
    sectionOpened,
    toggleSection,
  };
}
